import { useEffect, useState } from "react";
import { FiCheck, FiCopy, FiTruck } from "react-icons/fi";
import { apiFetch } from "../../utils/api";

const FLOW = ["Processing", "Shipped", "Delivered"];

// live status sent by AfterShip: [label, box colours]
const TAGS = {
  Pending: ["Waiting for courier", "border-gray-200 bg-gray-50 text-gray-700"],
  InfoReceived: ["Courier has your shipment details", "border-gray-200 bg-gray-50 text-gray-700"],
  InTransit: ["In transit", "border-blue-200 bg-blue-50 text-blue-800"],
  OutForDelivery: ["Out for delivery", "border-green-200 bg-green-50 text-green-800"],
  AttemptFail: ["Delivery attempt failed", "border-amber-200 bg-amber-50 text-amber-800"],
  Delivered: ["Delivered", "border-green-200 bg-green-50 text-green-800"],
  AvailableForPickup: ["Ready for pickup", "border-green-200 bg-green-50 text-green-800"],
  Exception: ["Delivery problem", "border-red-200 bg-red-50 text-red-700"],
  Expired: ["Tracking expired", "border-gray-200 bg-gray-50 text-gray-700"],
};

const fmt = (d) =>
  new Date(d).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

const shortDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

export default function OrderTracker({ orderId }) {
  const [t, setT] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/orders/${orderId}/tracking`)
      .then(({ ok, data }) => {
        if (!cancelled && ok) setT(data);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (!loaded) {
    return <div className="h-40 animate-pulse rounded-xl bg-gray-200" />;
  }
  if (!t) return null;

  const cancelled = t.status === "Cancelled";
  const current = FLOW.indexOf(t.status); // -1 if cancelled
  const when = (s) => t.history.find((h) => h.status === s)?.at;
  const tag = t.live ? TAGS[t.live.tag] || [t.live.tag, TAGS.Pending[1]] : null;

  const checkpoints = t.live?.checkpoints || [];
  const shown = showAll ? checkpoints : checkpoints.slice(0, 3);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(t.trackingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available, ignore */
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold text-gray-900">Track order</h2>

      {/* ---------- progress ---------- */}
      {cancelled ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          Tracking stopped because this order was cancelled.
        </p>
      ) : (
        <ol className="flex">
          {FLOW.map((s, i) => {
            const done = i <= current;
            const isCurrent = i === current;
            return (
              <li key={s} className="relative flex flex-1 flex-col items-center text-center">
                {i < FLOW.length - 1 && (
                  <span
                    className={`absolute left-1/2 top-3 h-0.5 w-full ${
                      i < current ? "bg-[#2f6b1f]" : "bg-gray-200"
                    }`}
                  />
                )}
                <span
                  className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    done
                      ? "bg-[#2f6b1f] text-white"
                      : "border-2 border-gray-300 bg-white text-gray-400"
                  } ${isCurrent ? "ring-4 ring-green-100" : ""}`}
                >
                  {done ? <FiCheck /> : i + 1}
                </span>
                <p
                  className={`mt-2 text-xs ${
                    done ? "font-semibold text-gray-900" : "text-gray-400"
                  }`}
                >
                  {s}
                </p>
                {when(s) && (
                  <p className="text-[11px] text-gray-500">{shortDate(when(s))}</p>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {/* ---------- courier + tracking number ---------- */}
      {(t.courier || t.trackingNumber) && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
          <FiTruck className="shrink-0 text-lg text-[#2f6b1f]" />
          <div className="min-w-0 flex-1">
            {t.courier && (
              <p className="text-xs capitalize text-gray-500">
                {t.courier.replace(/-/g, " ")}
              </p>
            )}
            {t.trackingNumber && (
              <p className="break-all text-sm font-medium text-gray-900">
                {t.trackingNumber}
              </p>
            )}
          </div>
          {t.trackingNumber && (
            <button
              type="button"
              onClick={copy}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
            >
              <FiCopy /> {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      )}

      {/* ---------- live status from the courier ---------- */}
      {tag && (
        <div className={`mt-4 rounded-lg border px-4 py-3 ${tag[1]}`}>
          <p className="text-sm font-semibold">{tag[0]}</p>
          {t.live.message && <p className="mt-0.5 text-xs opacity-90">{t.live.message}</p>}
        </div>
      )}

      {/* ---------- courier updates ---------- */}
      {checkpoints.length > 0 && (
        <div className="mt-5">
          <p className="mb-3 text-xs font-semibold text-gray-900">Courier updates</p>
          <ul className="ml-1.5 border-l border-gray-200">
            {shown.map((c, i) => (
              <li key={i} className="relative pb-4 pl-5 last:pb-0">
                <span
                  className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${
                    i === 0 ? "bg-[#2f6b1f]" : "bg-gray-300"
                  }`}
                />
                <p className="text-sm text-gray-800">{c.message}</p>
                <p className="text-xs text-gray-500">
                  {[c.location, c.time && fmt(c.time)].filter(Boolean).join(" · ")}
                </p>
              </li>
            ))}
          </ul>
          {checkpoints.length > 3 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 text-xs font-semibold text-[#2f6b1f] hover:underline"
            >
              {showAll ? "Show less" : `Show all ${checkpoints.length} updates`}
            </button>
          )}
        </div>
      )}

      {/* ---------- order history ---------- */}
      {t.history.length > 0 && (
        <details className="mt-5 border-t border-gray-100 pt-4">
          <summary className="cursor-pointer text-xs font-semibold text-gray-900">
            Order history
          </summary>
          <ul className="mt-3 flex flex-col gap-2">
            {[...t.history].reverse().map((h, i) => (
              <li key={i} className="text-sm text-gray-700">
                {h.note}
                <span className="block text-xs text-gray-500">{fmt(h.at)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
